import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import TableSortLabel from "@mui/material/TableSortLabel";
import TablePagination from "@mui/material/TablePagination";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Zoom from "@mui/material/Zoom";
import EditIcon from "@mui/icons-material/Edit";
import EventRepeatIcon from "@mui/icons-material/EventRepeat";
import SearchIcon from "@mui/icons-material/Search";
import Button from "@mui/material/Button";
import LoadingOverlay from "../../components/overlays/LoadingOverlay";
import AppNavigation from "../../components/navigation/appNavigation/AppNavigation";
import MainFooter from "../../components/footer/MainFooter";
import { commonErrorToastMessage } from "../../components/toastify/AllToastifyErrors";
import { getUserList } from "../../api/getUserList";
import AddEditUserModal from "./AddEditUserModal";
import ExtendExpiryModal from "./ExtendExpiryModal";
import { format } from "date-fns";
import "../../../assets/styles/Common.css";
import "./Utility.css";

const ADMIN_ROLE_ID = parseInt(process.env.REACT_APP_ADMIN_ROLE_ID || "2", 10);

const formatDateValue = (value) => {
  if (value == null || value === "") return "—";
  try {
    const d = new Date(value);
    return isNaN(d.getTime()) ? value : format(d, "MMM d, yyyy");
  } catch {
    return value;
  }
};

const formatBoolValue = (value) => (value ? "Yes" : "No");

const getSortValue = (row, colId) => {
  if (colId === "expiryDate") {
    const raw = row.expiryDate ?? row.ExpiryDate ?? "";
    const d = raw ? new Date(raw) : null;
    return d && !isNaN(d.getTime()) ? d.getTime() : 0;
  }
  return null;
};

const UserListPage = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.users);
  const [isLoading, setIsLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("add");
  const [editingUserId, setEditingUserId] = useState(null);
  const [extendModalOpen, setExtendModalOpen] = useState(false);
  const [extendUser, setExtendUser] = useState(null);

  const isAdmin = user?.RoleId === ADMIN_ROLE_ID;
  const userLoaded = user?.Id != null || user?.Email;

  useEffect(() => {
    if (userLoaded && !isAdmin) {
      navigate("/", { replace: true });
    }
  }, [userLoaded, isAdmin, navigate]);

  const fetchUsers = async () => {
    try {
      setIsLoading(true);
      const data = await getUserList();
      const list = Array.isArray(data) ? data : data?.users ?? [];
      setUsers(list);
    } catch (error) {
      console.error("Error fetching user list:", error);
      commonErrorToastMessage(
        error?.response?.data?.message || "Failed to load user list."
      );
      setUsers([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      fetchUsers();
    }
  }, [isAdmin]);

  const handleOpenAddUser = () => {
    setModalMode("add");
    setEditingUserId(null);
    setModalOpen(true);
  };

  const handleEdit = (row) => {
    const id = row.id;
    if (id == null) {
      commonErrorToastMessage("User id not found.");
      return;
    }
    setModalMode("edit");
    setEditingUserId(id);
    setModalOpen(true);
  };

  const handleExtend = (row) => {
    if (row?.id == null) {
      commonErrorToastMessage("User not found.");
      return;
    }
    setExtendUser(row);
    setExtendModalOpen(true);
  };

  const columns = useMemo(() => [
    { id: "name", label: "Name", align: "left", getValue: (row) => row.name ?? row.username ?? "—" },
    { id: "email", label: "Email", align: "left", getValue: (row) => row.email ?? "—" },
    { id: "accountType", label: "Type", align: "left", getValue: (row) => row.accountType ?? "—" },
    { id: "role", label: "Role", align: "left", getValue: (row) => row.role ?? row.Role ?? "—" },
    { id: "remainingQuestionSet", label: "Q Sets", align: "center", getValue: (row) => row.remainingQuestionSet ?? row.remainingQuestionSets ?? "—" },
    { id: "isPlanExpired", label: "Expired", align: "center", getValue: (row) => formatBoolValue(row.isPlanExpired ?? row.isPlanExpired) },
    { id: "buyer", label: "Buyer", align: "left", getValue: (row) => row.buyer ?? "—" },
    { id: "expiryDate", label: "Expiry", align: "center", getValue: (row) => formatDateValue(row.expiryDate ?? row.ExpiryDate) },
    { id: "planValidity", label: "Validity", align: "center", getValue: (row) => { const v = row.planValidity ?? "—"; if (!v || v === "—") return v; return String(v).replace(/\s*months?\s*$/i, "").trim() || "—"; } },
  ], []);

  const [searchTerm, setSearchTerm] = useState("");
  const [orderBy, setOrderBy] = useState("name");
  const [order, setOrder] = useState("asc");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const getRowSearchText = useCallback((row) => {
    const name = (row.name ?? row.username ?? "").toString().toLowerCase();
    const email = (row.email ?? "").toString().toLowerCase();
    const accountType = (row.accountType ?? "").toString().toLowerCase();
    const role = (row.role ?? row.Role ?? "").toString().toLowerCase();
    const remainingSet = (row.remainingQuestionSet ?? row.remainingQuestionSets ?? "").toString().toLowerCase();
    const planExpired = formatBoolValue(row.isPlanExpired).toLowerCase();
    const buyer = (row.buyer ?? "").toString().toLowerCase();
    return [name, email, accountType, role, remainingSet, planExpired, buyer].join(" ");
  }, []);

  const filteredUsers = useMemo(() => {
    if (!searchTerm.trim()) return users;
    const term = searchTerm.trim().toLowerCase();
    return users.filter((row) => getRowSearchText(row).includes(term));
  }, [users, searchTerm, getRowSearchText]);

  const sortedUsers = useMemo(() => {
    const sorted = [...filteredUsers].sort((a, b) => {
      const col = columns.find((c) => c.id === orderBy);
      if (!col) return 0;
      const sortValA = getSortValue(a, orderBy);
      const sortValB = getSortValue(b, orderBy);
      if (sortValA != null && sortValB != null) {
        return order === "asc" ? sortValA - sortValB : sortValB - sortValA;
      }
      const aVal = col.getValue(a);
      const bVal = col.getValue(b);
      const aNum = Number(aVal);
      const bNum = Number(bVal);
      if (!Number.isNaN(aNum) && !Number.isNaN(bNum)) {
        return order === "asc" ? aNum - bNum : bNum - aNum;
      }
      const aStr = String(aVal ?? "").toLowerCase();
      const bStr = String(bVal ?? "").toLowerCase();
      if (aStr < bStr) return order === "asc" ? -1 : 1;
      if (aStr > bStr) return order === "asc" ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [filteredUsers, orderBy, order, columns]);

  const paginatedUsers = useMemo(() => {
    const start = page * rowsPerPage;
    return sortedUsers.slice(start, start + rowsPerPage);
  }, [sortedUsers, page, rowsPerPage]);

  const handleSort = (columnId) => {
    const isAsc = orderBy === columnId && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(columnId);
    setPage(0);
  };

  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  if (userLoaded && !isAdmin) {
    return null;
  }

  return (
    <div>
      <AppNavigation />
      <div className="utility-page-wrapper custom-scrollbar">
        <div className="utility-page-inner">
          <div className="utility-search-add-row">
            <TextField
              size="small"
              placeholder="Search: Name, Email, Type, Role, Q Sets, Expired, Buyer"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(0);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#777E8D" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              width: "100%",
              maxWidth: 420,
              minWidth: 280,
              "& .MuiOutlinedInput-root": {
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#F4833D" },
                "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#F4833D" },
              },
              "& .MuiInputLabel-root.Mui-focused": { color: "#F4833D" },
            }}
            />
            <Button
              variant="contained"
              onClick={handleOpenAddUser}
              sx={{
                backgroundColor: "#F4833D",
                color: "#fff",
                "&:hover": { backgroundColor: "#e06a21" },
              }}
            >
              Add user
            </Button>
          </div>

          {users.length === 0 && !isLoading ? (
            <p className="utility-page-empty">
              No users found.
            </p>
          ) : filteredUsers.length === 0 ? (
            <p className="utility-page-empty">
              No users match your search.
            </p>
          ) : (
            <Paper sx={{ boxShadow: "0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)", border: "1px solid #B0B5C1" }}>
              <TableContainer sx={{ overflowX: "auto" }}>
                <Table size="small" stickyHeader aria-label="User list table">
                  <TableHead>
                    <TableRow>
                      {columns.map((col) => (
                        <TableCell
                          key={col.id}
                          align={col.align || "left"}
                          sortDirection={orderBy === col.id ? order : false}
                          sx={{
                            fontWeight: 600,
                            backgroundColor: "#042739",
                            color: "white",
                            borderColor: "#042739",
                            "& .MuiTableSortLabel-root": {
                              width: "100%",
                              justifyContent: (col.align || "left") === "center" ? "center" : "flex-start",
                            },
                          }}
                        >
                          <TableSortLabel
                            active={orderBy === col.id}
                            direction={orderBy === col.id ? order : "asc"}
                            onClick={() => handleSort(col.id)}
                            sx={{ color: "white", "& .MuiTableSortLabel-icon": { color: "white" }, "&:hover": { color: "#F4833D" }, "&.Mui-active": { color: "#F4833D" } }}
                          >
                            {col.label}
                          </TableSortLabel>
                        </TableCell>
                      ))}
                      <TableCell align="center" sx={{ fontWeight: 600, backgroundColor: "#042739", color: "white", borderColor: "#042739", minWidth: 88, whiteSpace: "nowrap" }}>
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedUsers.map((row, index) => (
                      <TableRow key={row.id ?? row.email ?? index} hover sx={{ "&:hover": { backgroundColor: "rgba(244, 131, 61, 0.08)" } }}>
                        {columns.map((col) => (
                          <TableCell key={col.id} align={col.align || "left"} sx={{ color: "#212529" }}>
                            {col.getValue(row)}
                          </TableCell>
                        ))}
                        <TableCell align="center" sx={{ minWidth: 88, whiteSpace: "nowrap", paddingLeft: 0, paddingRight: 0 }}>
                          <span className="utility-table-actions-buttons">
                            <Tooltip title="Edit user" arrow TransitionComponent={Zoom} TransitionProps={{ timeout: 300 }}>
                              <IconButton size="small" onClick={() => handleEdit(row)} aria-label="Edit user" sx={{ color: "#F4833D", padding: "4px", "&:hover": { backgroundColor: "rgba(244, 131, 61, 0.12)", color: "#e06a21" } }}>
                                <EditIcon sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Extend plan" arrow TransitionComponent={Zoom} TransitionProps={{ timeout: 300 }}>
                              <IconButton size="small" onClick={() => handleExtend(row)} aria-label="Extend plan" sx={{ color: "#042739", padding: "4px", "&:hover": { backgroundColor: "rgba(4, 39, 57, 0.08)", color: "#042739" } }}>
                                <EventRepeatIcon sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                          </span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={sortedUsers.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25, 50]}
                showFirstButton
                showLastButton
                sx={{
                  borderTop: "1px solid #B0B5C1",
                  ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows": { color: "#777E8D" },
                  ".MuiIconButton-root.Mui-selected": { backgroundColor: "rgba(244, 131, 61, 0.2)", color: "#F4833D" },
                  ".MuiIconButton-root:not(.Mui-disabled):hover": { color: "#F4833D" },
                }}
              />
            </Paper>
          )}
        </div>
      </div>
      <MainFooter />
      {isLoading && <LoadingOverlay />}

      <AddEditUserModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        mode={modalMode}
        editingUserId={editingUserId}
        onSuccess={fetchUsers}
      />
      <ExtendExpiryModal
        open={extendModalOpen}
        onClose={() => { setExtendModalOpen(false); setExtendUser(null); }}
        user={extendUser}
        onSuccess={fetchUsers}
      />
    </div>
  );
};

export default UserListPage;
